"use client";

import { useCallback, useEffect, useState } from "react";
import { authFetch } from "@/lib/authFetch";

type FollowEntry = { leaderId: string; enabled: boolean; label?: string | null };

/**
 * Map of `polymarket_address.toLowerCase() -> { leaderId, enabled }`
 * for the user's copy-trading follows, plus a stable `handleToggleCopy`
 * that flips pause/resume on a given wallet.
 *
 * `handleToggleCopy` is `useCallback`'d so EventCard's `onToggleCopy`
 * prop ref stays stable across feed re-renders (poll, sort, expand).
 * Without that, every poll tick would invalidate every memoized row.
 */
export function useFollowedTraders() {
  const [followedTraders, setFollowedTraders] = useState<Map<string, FollowEntry>>(new Map());

  const fetchFollowing = useCallback(async () => {
    try {
      const res = await authFetch("/api/copytrading/following");
      if (res.ok) {
        const data = await res.json();
        const map = new Map<string, FollowEntry>();
        for (const leader of data.following || []) {
          if (leader.polymarket_address) {
            map.set(leader.polymarket_address.toLowerCase(), {
              leaderId: leader.leader_id,
              enabled: leader.copy_enabled,
              // private per-user tag (user_trader_labels) — rows prefer it
              label: leader.custom_label ?? null,
            });
          }
        }
        setFollowedTraders(map);
      }
    } catch {}
  }, []);

  useEffect(() => {
    fetchFollowing();
  }, [fetchFollowing]);

  const handleToggleCopy = useCallback(async (wallet: string) => {
    const info = followedTraders.get(wallet.toLowerCase());
    if (!info) return;
    const endpoint = info.enabled ? "/api/copytrading/pause" : "/api/copytrading/resume";
    try {
      const res = await authFetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ leaderId: info.leaderId }),
      });
      if (res.ok) {
        setFollowedTraders((prev) => {
          const next = new Map(prev);
          next.set(wallet.toLowerCase(), { ...info, enabled: !info.enabled });
          return next;
        });
      }
    } catch {}
  }, [followedTraders]);

  return { followedTraders, fetchFollowing, handleToggleCopy };
}
